import {
  getSharedI18nInstance,
  resolveSupportedLocale,
  type SupportedLocale,
} from "@bitsentry-ce/i18n";

import enUSSettings from "./locales/en-US/settings.json";
import enUSAuth from "./locales/en-US/auth.json";
import enUSCommon from "./locales/en-US/common.json";
import enUSDashboard from "./locales/en-US/dashboard.json";
import enUSIncidents from "./locales/en-US/incidents.json";
import enUSRunbooks from "./locales/en-US/runbooks.json";

type DashboardNamespace =
  | "auth"
  | "common"
  | "dashboard"
  | "incidents"
  | "runbooks"
  | "settings";
type DashboardBundle = Record<string, unknown>;
type NamespaceLoader = () => Promise<{ default: DashboardBundle }>;
type DeferredLocale = Exclude<SupportedLocale, "en-US">;

const defaultResources = {
  auth: enUSAuth,
  common: enUSCommon,
  dashboard: enUSDashboard,
  incidents: enUSIncidents,
  runbooks: enUSRunbooks,
  settings: enUSSettings,
} as const;

const deferredLocaleLoaders = {
  "en-GB": {
    auth: () => import("./locales/en-GB/auth.json"),
    common: () => import("./locales/en-GB/common.json"),
    dashboard: () => import("./locales/en-GB/dashboard.json"),
    incidents: () => import("./locales/en-GB/incidents.json"),
    runbooks: () => import("./locales/en-GB/runbooks.json"),
    settings: () => import("./locales/en-GB/settings.json"),
  },
  "en-AU": {
    auth: () => import("./locales/en-AU/auth.json"),
    common: () => import("./locales/en-AU/common.json"),
    dashboard: () => import("./locales/en-AU/dashboard.json"),
    incidents: () => import("./locales/en-AU/incidents.json"),
    runbooks: () => import("./locales/en-AU/runbooks.json"),
    settings: () => import("./locales/en-AU/settings.json"),
  },
  "fr-FR": {
    auth: () => import("./locales/fr-FR/auth.json"),
    common: () => import("./locales/fr-FR/common.json"),
    dashboard: () => import("./locales/fr-FR/dashboard.json"),
    incidents: () => import("./locales/fr-FR/incidents.json"),
    runbooks: () => import("./locales/fr-FR/runbooks.json"),
    settings: () => import("./locales/fr-FR/settings.json"),
  },
  "id-ID": {
    auth: () => import("./locales/id-ID/auth.json"),
    common: () => import("./locales/id-ID/common.json"),
    dashboard: () => import("./locales/id-ID/dashboard.json"),
    incidents: () => import("./locales/id-ID/incidents.json"),
    runbooks: () => import("./locales/id-ID/runbooks.json"),
    settings: () => import("./locales/id-ID/settings.json"),
  },
  "zh-CN": {
    auth: () => import("./locales/zh-CN/auth.json"),
    common: () => import("./locales/zh-CN/common.json"),
    dashboard: () => import("./locales/zh-CN/dashboard.json"),
    incidents: () => import("./locales/zh-CN/incidents.json"),
    runbooks: () => import("./locales/zh-CN/runbooks.json"),
    settings: () => import("./locales/zh-CN/settings.json"),
  },
} satisfies Record<DeferredLocale, Record<DashboardNamespace, NamespaceLoader>>;

const loadedLocales = new Set<SupportedLocale>(["en-US"]);
const loadingLocales = new Map<SupportedLocale, Promise<void>>();

type SharedI18nInstance = ReturnType<typeof getSharedI18nInstance>;

let registeredInstance: SharedI18nInstance | null = null;

function registerBundles(
  i18n: SharedI18nInstance,
  locale: SupportedLocale,
  bundles: Partial<Record<DashboardNamespace, DashboardBundle>>,
): void {
  for (const [namespace, bundle] of Object.entries(bundles)) {
    if (bundle === undefined) continue;
    i18n.addResourceBundle(locale, namespace, bundle, true, true);
  }
}

async function loadLocale(
  locale: DeferredLocale,
  i18n: SharedI18nInstance,
): Promise<void> {
  if (loadedLocales.has(locale)) return;

  const existingLoad = loadingLocales.get(locale);
  if (existingLoad !== undefined) {
    return existingLoad;
  }

  const loaders = deferredLocaleLoaders[locale];
  const load = Promise.all(
    Object.entries(loaders).map(async ([namespace, loadNamespace]) => {
      const module = await loadNamespace();
      return [namespace, module.default] as const;
    }),
  ).then((entries) => {
    registerBundles(i18n, locale, Object.fromEntries(entries));
    loadedLocales.add(locale);
  });

  loadingLocales.set(locale, load);
  try {
    await load;
  } finally {
    loadingLocales.delete(locale);
  }
}

function loadCurrentLocale(i18n: SharedI18nInstance): void {
  const locale = resolveSupportedLocale(i18n.language);
  if (locale === null || locale === "en-US") return;

  void loadLocale(locale, i18n).catch((error: unknown) => {
    console.warn(`[i18n] Failed to load dashboard locale ${locale}`, error);
  });
}

export function registerDashboardTranslations(): void {
  const i18n = getSharedI18nInstance();
  if (registeredInstance === i18n) return;

  registerBundles(i18n, "en-US", defaultResources);
  loadCurrentLocale(i18n);
  i18n.on("initialized", () => loadCurrentLocale(i18n));
  i18n.on("languageChanged", () => loadCurrentLocale(i18n));
  registeredInstance = i18n;
}
